// cartReducer.ts

import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createAction,
} from "@reduxjs/toolkit";
import { validateCart, findOrCreateCart } from "../lib/cartApis";

// Define types for cart items and groups
export interface CartProduct {
  cartCustomId: string;
  productId: string;
  quantity: number;
  price: number;
  finalPrice: number;
  product: {
    id: string;
    name: string;
    sku?: string;
    stock: number;
    reservedStock?: number;
    [key: string]: any;
  };
  isOptimistic?: boolean;
  [key: string]: any;
}

export interface CartGroup {
  id: string;
  deliveryMethod?: string;
  deliveryDate?: string;
  deliveryFee: number;
  subTotal: number;
  discountValue: number;
  groupTotal: number;
  totalWeight?: number;
  products: CartProduct[];
  deliveryFeeZone?: {
    documentId: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export interface OptimisticProduct {
  product: CartProduct;
  operationId: string;
  timestamp: number;
}

export interface CartOperationState {
  inProgress: boolean;
  startTime?: number;
  groups: Record<string, boolean>;
  items: Record<string, boolean>;
}

export interface CartState {
  // Cart details
  cartId: string | null;
  cartStatus: string;
  cartVersion: number;
  totalPrice: number;
  totalDeliveryFee: number;
  discountValue: number;
  finalTotal: number;
  groups: CartGroup[];

  // UI state
  isLoading: boolean;
  error: string | null;

  // Optimistic updates tracking
  optimisticProducts: OptimisticProduct[];
  optimisticTimeout: number;
  pendingUpdates: Record<string, { quantity: number; notes?: string }>;

  // Legacy loading state properties (deprecated but kept for backward compatibility)
  isUpdatingFees: boolean;
  loadingStateStartTime: number;
  minLoadingDuration: number;
  groupUpdatingFees: Record<string, boolean>;
  groupUpdatingDeliveryDate: Record<string, boolean>;
  pendingOperations: number;
  pendingFeeApiCalls: number;
  isResetScheduled: boolean;
  lastFeeLoadingStartTime?: number;
  lastFeeLoadingVersionStart?: number;

  // Operation-based loading states (new approach)
  operations: {
    locationUpdate: CartOperationState;
    feeCalculation: CartOperationState;
    dateUpdate: CartOperationState;
    itemUpdate: CartOperationState;
  };

  // UI display flags
  ui: {
    showGlobalOverlay: boolean;
    showGroupOverlays: Record<string, boolean>;
    minimumDisplayTimeEnds: number | null;
  };
}

// Initial state structure
const initialState: CartState = {
  // Cart details
  cartId: null,
  cartStatus: "inactive",
  cartVersion: 1,
  totalPrice: 0,
  totalDeliveryFee: 0,
  discountValue: 0,
  finalTotal: 0,
  groups: [],

  // UI state
  isLoading: false,
  error: null,

  // Optimistic updates tracking
  optimisticProducts: [],
  optimisticTimeout: 15000,
  pendingUpdates: {},

  // Legacy loading state properties
  isUpdatingFees: false,
  loadingStateStartTime: 0,
  minLoadingDuration: 1500,
  groupUpdatingFees: {},
  groupUpdatingDeliveryDate: {},
  pendingOperations: 0,
  pendingFeeApiCalls: 0,
  isResetScheduled: false,

  // Operation-based state (new approach)
  operations: {
    locationUpdate: { inProgress: false, groups: {}, items: {} },
    feeCalculation: { inProgress: false, groups: {}, items: {} },
    dateUpdate: { inProgress: false, groups: {}, items: {} },
    itemUpdate: { inProgress: false, groups: {}, items: {} },
  },

  // UI flags (new approach)
  ui: {
    showGlobalOverlay: false,
    showGroupOverlays: {},
    minimumDisplayTimeEnds: null,
  },
};

// Define properly typed action creators for custom actions
export const setTemporaryCartState = createAction<any>(
  "SET_TEMPORARY_CART_STATE"
);
export const fetchCartSuccess = createAction<any>("FETCH_CART_SUCCESS");
export const resetGroupDeliveryFeeLoadingAction = createAction<string>(
  "RESET_GROUP_DELIVERY_FEE_LOADING"
);

// Helper function to generate consistent cartCustomId
// Export the function to avoid unused variable warning
export function generateCartCustomId(
  productId: string,
  options: {
    groupingAttributes?: Array<{ name: string; value: string }>;
    customizableOptions?: Array<{ id: string; value: any }>;
  } = {}
): string {
  const { groupingAttributes = [], customizableOptions = [] } = options;

  // Create string from grouping attributes
  const attrString = groupingAttributes
    .map((attr) => `${attr.name}:${attr.value}`)
    .join("|");

  // Create string from customization options
  const optionsString = customizableOptions
    .filter((opt) => opt.value !== undefined && opt.value !== null)
    .map((opt) => `${opt.id}:${opt.value}`)
    .join("|");

  // Combine with productId
  let id = `${productId}`;
  if (attrString) id += `-${attrString}`;
  if (optionsString) id += `-${optionsString}`;

  return id;
}

// Thunk to validate cart on page load
export const validateExistingCart = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>("cart/validateExistingCart", async (cartId, { rejectWithValue }) => {
  try {
    if (!cartId) return { isValid: false };

    const response = await validateCart(cartId);
    return response;
  } catch (error) {
    console.error("❌ Error validating cart:", error);
    return rejectWithValue("Failed to validate cart.");
  }
});

// Thunk to fetch or create cart
export const fetchCart = createAsyncThunk<any, void, { rejectValue: string }>(
  "cart/fetchCart",
  async (_, { rejectWithValue }) => {
    try {
      return await findOrCreateCart();
    } catch (error) {
      console.error("❌ Error fetching cart:", error);
      return rejectWithValue("Failed to fetch cart.");
    }
  }
);

// Cart slice
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearCart: () => initialState,

    // Basic cart data operations
    setCartData: (state, action: PayloadAction<any>) => {
      state.cartId = action.payload.cartId;
      state.cartStatus = action.payload.cartStatus;
      state.cartVersion = action.payload.cartVersion;
      state.totalPrice = action.payload.totalPrice;
      state.totalDeliveryFee = action.payload.totalDeliveryFee;
      state.discountValue = action.payload.discountValue;
      state.finalTotal = action.payload.finalTotal;
      state.groups = action.payload.groups;
    },

    updateCartVersion: (state, action: PayloadAction<number>) => {
      state.cartVersion = action.payload;
    },

    // Optimistic update tracking
    addOptimisticProduct: (
      state,
      action: PayloadAction<{
        product: CartProduct;
        operationId: string;
      }>
    ) => {
      const { product, operationId } = action.payload;
      state.optimisticProducts.push({
        product,
        operationId,
        timestamp: Date.now(),
      });
    },

    removeOptimisticProduct: (
      state,
      action: PayloadAction<{
        productId?: string;
        operationId?: string;
        cartCustomId?: string;
      }>
    ) => {
      const { productId, operationId, cartCustomId } = action.payload;
      state.optimisticProducts = state.optimisticProducts.filter(
        (optimisticProduct) =>
          (productId && optimisticProduct.product.productId !== productId) ||
          (operationId && optimisticProduct.operationId !== operationId) ||
          (cartCustomId &&
            optimisticProduct.product.cartCustomId !== cartCustomId)
      );
    },

    cleanupStaleOptimisticUpdates: (state) => {
      const now = Date.now();
      state.optimisticProducts = state.optimisticProducts.filter(
        (optimisticProduct) =>
          now - optimisticProduct.timestamp < state.optimisticTimeout
      );
    },

    // Item quantity tracking
    updateCartItemQuantity: (
      state,
      action: PayloadAction<{
        cartCustomId: string;
        quantity: number;
        notes?: string;
        price?: number;
        finalPrice?: number;
      }>
    ) => {
      const { cartCustomId, quantity, notes, price, finalPrice } =
        action.payload;
      const group = state.groups.find((group) =>
        group.products.some((product) => product.cartCustomId === cartCustomId)
      );
      if (group) {
        const product = group.products.find(
          (product) => product.cartCustomId === cartCustomId
        );
        if (product) {
          product.quantity = quantity;
          if (notes) product.notes = notes;
          if (price) product.price = price;
          if (finalPrice) product.finalPrice = finalPrice;
        }
      }
    },

    // Legacy operations tracking - maintained for backward compatibility
    incrementPendingOperations: (state) => {
      state.pendingOperations += 1;
      console.log(
        `[CART-OPERATIONS] Incremented pending operations: ${state.pendingOperations}`
      );
    },

    decrementPendingOperations: (state) => {
      state.pendingOperations = Math.max(0, state.pendingOperations - 1);
      console.log(
        `[CART-OPERATIONS] Decremented pending operations: ${state.pendingOperations}`
      );
    },

    incrementPendingFeeApiCalls(state) {
      state.pendingFeeApiCalls += 1;
      console.log(
        `[REDUX-API] Incremented pending fee API calls: ${state.pendingFeeApiCalls}`
      );
    },

    decrementPendingFeeApiCalls(state) {
      state.pendingFeeApiCalls = Math.max(0, state.pendingFeeApiCalls - 1);
      console.log(
        `[REDUX-API] Decremented pending fee API calls: ${state.pendingFeeApiCalls}`
      );

      // If we have a scheduled reset and no more pending API calls, execute the reset
      if (state.isResetScheduled && state.pendingFeeApiCalls === 0) {
        console.log(
          "[REDUX-API] Executing scheduled reset of loading state (no more pending API calls)"
        );
        state.isUpdatingFees = false;
        state.groupUpdatingFees = {};
        state.isResetScheduled = false;
      }
    },

    // Legacy loading state tracking - maintained for backward compatibility
    setDeliveryFeesLoadingState(state, action: PayloadAction<boolean>) {
      state.isUpdatingFees = action.payload;
      if (action.payload) {
        state.loadingStateStartTime = Date.now();
      }
    },

    setGroupDeliveryFeeLoading: (
      state,
      action: PayloadAction<{
        groupId: string;
        isLoading: boolean;
      }>
    ) => {
      const { groupId, isLoading } = action.payload;
      state.groupUpdatingFees[groupId] = isLoading;
    },

    setGroupDeliveryDateLoading: (
      state,
      action: PayloadAction<{
        groupId: string;
        isLoading: boolean;
      }>
    ) => {
      const { groupId, isLoading } = action.payload;
      state.groupUpdatingDeliveryDate[groupId] = isLoading;
    },

    resetGroupDeliveryDateLoading: (state, action: PayloadAction<string>) => {
      const groupId = action.payload;
      state.groupUpdatingDeliveryDate[groupId] = false;
    },

    resetGroupDeliveryFeeLoading: (state, action: PayloadAction<string>) => {
      const groupId = action.payload;
      state.groupUpdatingFees[groupId] = false;
    },

    resetDeliveryFeesLoading: (
      state,
      action: PayloadAction<{ force?: boolean } | undefined>
    ) => {
      const { force } = action.payload || {};
      if (force || state.pendingFeeApiCalls === 0) {
        state.isUpdatingFees = false;
        state.groupUpdatingFees = {};
      } else {
        state.isResetScheduled = true;
      }
    },

    // NEW OPERATION-BASED ACTIONS

    // Location update operations
    startLocationUpdate(state, action: PayloadAction<string[]>) {
      const groupIds = action.payload;

      // Make a copy of the current operations state
      const operationsState = {
        ...state.operations.locationUpdate,
        inProgress: true,
        startTime: Date.now(),
      };

      // Add group IDs to the groups object
      const groupsObject: Record<string, boolean> = {};
      groupIds.forEach((groupId) => {
        groupsObject[groupId] = true;
      });
      operationsState.groups = groupsObject;

      // Update the state safely without recursive calls
      state.operations.locationUpdate = operationsState;

      // Set UI states
      state.ui.showGlobalOverlay = true;
      state.ui.minimumDisplayTimeEnds = Date.now() + 1500; // 1.5s minimum

      // For compatibility with existing code
      state.isUpdatingFees = true;
      state.loadingStateStartTime = Date.now();

      // Update group updating states
      if (!state.groupUpdatingFees) {
        state.groupUpdatingFees = {};
      }

      groupIds.forEach((groupId) => {
        state.groupUpdatingFees[groupId] = true;
      });
    },

    completeLocationUpdate(state) {
      // Check if we need to maintain the minimum display time
      const now = Date.now();
      const minimumTimeEnds = state.ui.minimumDisplayTimeEnds || now;
      const minimumTimeReached = now >= minimumTimeEnds;

      if (minimumTimeReached) {
        state.operations.locationUpdate.inProgress = false;
        state.operations.locationUpdate.groups = {};
        state.ui.showGlobalOverlay = false;
        state.ui.minimumDisplayTimeEnds = null;

        // For compatibility with existing code
        state.isUpdatingFees = false;
        state.groupUpdatingFees = {};
      } else {
        // Schedule cleanup after minimum time
        const delay = minimumTimeEnds - now;
        setTimeout(() => {
          // This will dispatch completeLocationUpdate again
          // but minimumTimeReached will be true
        }, delay);
      }
    },

    // Fee calculation operations
    startFeeCalculation(state, action: PayloadAction<string[]>) {
      const groupIds = action.payload;
      state.operations.feeCalculation.inProgress = true;
      state.operations.feeCalculation.startTime = Date.now();

      // Set individual group states and overlays
      groupIds.forEach((groupId) => {
        state.operations.feeCalculation.groups[groupId] = true;
        state.ui.showGroupOverlays[groupId] = true;

        // For compatibility with existing code
        if (!state.groupUpdatingFees) state.groupUpdatingFees = {};
        state.groupUpdatingFees[groupId] = true;
      });

      // Set global loading state
      state.isUpdatingFees = true;
    },

    completeFeeCalculation(state, action: PayloadAction<string>) {
      const groupId = action.payload;

      if (state.operations.feeCalculation.groups[groupId]) {
        state.operations.feeCalculation.groups[groupId] = false;

        // Check if all groups are done
        const anyGroupStillCalculating = Object.values(
          state.operations.feeCalculation.groups
        ).some(Boolean);

        if (!anyGroupStillCalculating) {
          state.operations.feeCalculation.inProgress = false;
        }

        // Remove overlay after minimum time
        const now = Date.now();
        const startTime =
          state.operations.feeCalculation.startTime || now - 1500;
        const elapsedTime = now - startTime;
        const minDisplayTime = 1500;

        if (elapsedTime >= minDisplayTime) {
          state.ui.showGroupOverlays[groupId] = false;
        } else {
          setTimeout(() => {
            // Dispatch action to clear overlay
          }, minDisplayTime - elapsedTime);
        }

        // For compatibility with existing code
        if (state.groupUpdatingFees && state.groupUpdatingFees[groupId]) {
          state.groupUpdatingFees[groupId] = false;

          const anyGroupStillUpdating = Object.values(
            state.groupUpdatingFees
          ).some(Boolean);

          if (
            !anyGroupStillUpdating &&
            !state.operations.locationUpdate.inProgress
          ) {
            state.isUpdatingFees = false;
          }
        }
      }
    },

    // Date update operations
    startDateUpdate(state, action: PayloadAction<string>) {
      const groupId = action.payload;
      state.operations.dateUpdate.inProgress = true;
      state.operations.dateUpdate.groups[groupId] = true;
      state.ui.showGroupOverlays[groupId] = true;

      // For compatibility with existing code
      if (!state.groupUpdatingDeliveryDate)
        state.groupUpdatingDeliveryDate = {};
      state.groupUpdatingDeliveryDate[groupId] = true;
      state.isUpdatingFees = true;
    },

    completeDateUpdate(state, action: PayloadAction<string>) {
      const groupId = action.payload;
      state.operations.dateUpdate.groups[groupId] = false;

      // Check if all date updates are done
      const anyGroupStillUpdating = Object.values(
        state.operations.dateUpdate.groups
      ).some(Boolean);

      if (!anyGroupStillUpdating) {
        state.operations.dateUpdate.inProgress = false;
      }

      state.ui.showGroupOverlays[groupId] = false;

      // For compatibility with existing code
      if (
        state.groupUpdatingDeliveryDate &&
        state.groupUpdatingDeliveryDate[groupId]
      ) {
        state.groupUpdatingDeliveryDate[groupId] = false;

        const anyGroupUpdatingDate = Object.values(
          state.groupUpdatingDeliveryDate || {}
        ).some(Boolean);

        const anyGroupUpdatingFees = Object.values(
          state.groupUpdatingFees || {}
        ).some(Boolean);

        if (
          !anyGroupUpdatingDate &&
          !anyGroupUpdatingFees &&
          !state.operations.locationUpdate.inProgress
        ) {
          state.isUpdatingFees = false;
        }
      }
    },

    // Item operations - fixed by properly initializing operations if they're missing
    startItemUpdate(
      state,
      action: PayloadAction<{ itemId: string; groupId: string }>
    ) {
      const { itemId, groupId } = action.payload;

      // Ensure operations are properly initialized
      if (!state.operations) {
        state.operations = {
          locationUpdate: { inProgress: false, groups: {}, items: {} },
          feeCalculation: { inProgress: false, groups: {}, items: {} },
          dateUpdate: { inProgress: false, groups: {}, items: {} },
          itemUpdate: { inProgress: false, groups: {}, items: {} },
        };
      }

      // Ensure itemUpdate exists
      if (!state.operations.itemUpdate) {
        state.operations.itemUpdate = {
          inProgress: false,
          groups: {},
          items: {},
        };
      }

      // Now safely set values
      state.operations.itemUpdate.inProgress = true;
      state.operations.itemUpdate.items =
        state.operations.itemUpdate.items || {};
      state.operations.itemUpdate.items[itemId] = true;

      // Ensure feeCalculation exists
      if (!state.operations.feeCalculation) {
        state.operations.feeCalculation = {
          inProgress: false,
          groups: {},
          items: {},
        };
      }

      // Mark the fee calculation as pending for this group
      state.operations.feeCalculation.groups =
        state.operations.feeCalculation.groups || {};
      state.operations.feeCalculation.groups[groupId] = true;
      state.operations.feeCalculation.inProgress = true;

      // Ensure UI state is initialized
      if (!state.ui) {
        state.ui = {
          showGlobalOverlay: false,
          showGroupOverlays: {},
          minimumDisplayTimeEnds: null,
        };
      }

      // Show group overlay
      state.ui.showGroupOverlays = state.ui.showGroupOverlays || {};
      state.ui.showGroupOverlays[groupId] = true;

      // For compatibility with existing code
      if (!state.groupUpdatingFees) state.groupUpdatingFees = {};
      state.groupUpdatingFees[groupId] = true;
      state.isUpdatingFees = true;
    },

    completeItemUpdate(
      state,
      action: PayloadAction<{ itemId: string; groupId: string }>
    ) {
      const { itemId } = action.payload;
      state.operations.itemUpdate.items[itemId] = false;

      // Check if all item updates are done
      const anyItemStillUpdating = Object.values(
        state.operations.itemUpdate.items
      ).some(Boolean);

      if (!anyItemStillUpdating) {
        state.operations.itemUpdate.inProgress = false;
      }
    },

    // Fix clearAllOperations to ensure all fields are properly initialized
    clearAllOperations(state) {
      state.operations = {
        locationUpdate: { inProgress: false, groups: {}, items: {} },
        feeCalculation: { inProgress: false, groups: {}, items: {} },
        dateUpdate: { inProgress: false, groups: {}, items: {} },
        itemUpdate: { inProgress: false, groups: {}, items: {} },
      };

      state.ui = {
        showGlobalOverlay: false,
        showGroupOverlays: {},
        minimumDisplayTimeEnds: null,
      };

      // For compatibility with existing code
      state.isUpdatingFees = false;
      state.groupUpdatingFees = {};
      state.groupUpdatingDeliveryDate = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Cart validation
      .addCase(validateExistingCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateExistingCart.fulfilled, (state, action) => {
        const {
          cartId,
          cartStatus,
          cartVersion,
          totalPrice,
          totalDeliveryFee,
          discountValue,
          finalTotal,
          groups,
        } = action.payload;
        state.cartId = cartId;
        state.cartStatus = cartStatus;
        state.cartVersion = cartVersion;
        state.totalPrice = totalPrice;
        state.totalDeliveryFee = totalDeliveryFee;
        state.discountValue = discountValue;
        state.finalTotal = finalTotal;
        state.groups = groups;
        state.isLoading = false;
      })
      .addCase(validateExistingCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to validate cart.";
      })

      // Fetch cart
      .addCase(fetchCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        const {
          cartId,
          cartStatus,
          cartVersion,
          totalPrice,
          totalDeliveryFee,
          discountValue,
          finalTotal,
          groups,
        } = action.payload;
        state.cartId = cartId;
        state.cartStatus = cartStatus;
        state.cartVersion = cartVersion;
        state.totalPrice = totalPrice;
        state.totalDeliveryFee = totalDeliveryFee;
        state.discountValue = discountValue;
        state.finalTotal = finalTotal;
        state.groups = groups;
        state.isLoading = false;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch cart.";
      })

      // Custom action handlers - now using properly typed action creators
      .addCase(setTemporaryCartState, (state, action) => {
        const {
          cartId,
          cartStatus,
          cartVersion,
          totalPrice,
          totalDeliveryFee,
          discountValue,
          finalTotal,
          groups,
        } = action.payload;
        state.cartId = cartId;
        state.cartStatus = cartStatus;
        state.cartVersion = cartVersion;
        state.totalPrice = totalPrice;
        state.totalDeliveryFee = totalDeliveryFee;
        state.discountValue = discountValue;
        state.finalTotal = finalTotal;
        state.groups = groups;
      })
      .addCase(fetchCartSuccess, (state, action) => {
        const {
          cartId,
          cartStatus,
          cartVersion,
          totalPrice,
          totalDeliveryFee,
          discountValue,
          finalTotal,
          groups,
        } = action.payload;
        state.cartId = cartId;
        state.cartStatus = cartStatus;
        state.cartVersion = cartVersion;
        state.totalPrice = totalPrice;
        state.totalDeliveryFee = totalDeliveryFee;
        state.discountValue = discountValue;
        state.finalTotal = finalTotal;
        state.groups = groups;
      })
      .addCase(resetGroupDeliveryFeeLoadingAction, (state, action) => {
        const groupId = action.payload;
        state.groupUpdatingFees[groupId] = false;
      });
  },
});

// Export all actions including new ones
export const {
  // Basic state operations
  clearCart,
  setCartData: setCart,
  updateCartVersion,

  // Optimistic update tracking
  addOptimisticProduct,
  removeOptimisticProduct,
  cleanupStaleOptimisticUpdates,
  updateCartItemQuantity,

  // Legacy API tracking
  incrementPendingOperations,
  decrementPendingOperations,
  incrementPendingFeeApiCalls,
  decrementPendingFeeApiCalls,

  // Legacy loading tracking - maintained for backward compatibility
  setDeliveryFeesLoadingState,
  resetDeliveryFeesLoading,
  setGroupDeliveryFeeLoading,
  resetGroupDeliveryFeeLoading,
  setGroupDeliveryDateLoading,
  resetGroupDeliveryDateLoading,

  // New operation-based actions
  startLocationUpdate,
  completeLocationUpdate,
  startFeeCalculation,
  completeFeeCalculation,
  startDateUpdate,
  completeDateUpdate,
  startItemUpdate,
  completeItemUpdate,
  clearAllOperations,
} = cartSlice.actions;

// Map setDeliveryFeesLoadingState to setDeliveryFeesLoading for backward compatibility
export const setDeliveryFeesLoading = setDeliveryFeesLoadingState;

export default cartSlice.reducer;
